import i18nFactory from '@yandex-int/i18n';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import 'rxjs/add/operator/do';
import { catchError, mergeMap } from 'rxjs/operators';

import { ShowCommonDataFetchError, showCommonDataFetchError } from '../../actions/common';
import { Action } from '../../actions/index';
import {
    ADD_REPORT_DOMAIN,
    FETCH_REPORTS_DOMAIN,
    fetchReportsDomain,
    FetchReportsDomain,
    stopReportsDomainLoading,
    StopReportsDomainLoading,
    updateReportsDomain,
    UpdateReportsDomain
} from '../../actions/reportsDomain';
import {
    mapReportsDomainApiDataToClientData,
    mapReportsDomainDatesToApiFormat
} from '../../mappers/reportsDomain/reportsDomain';
import { ApiMethodExecutingError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { getReportsSuccessTexts } from '../../service/TextStoreHelpers/TextStoreHelpers';
import { GdOfflineReportResultRequestInput, OfflineReportTypeInput } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { State } from '../../typings/state';

import * as keyset from './reportsDomain.i18n';

const i18n = i18nFactory(keyset);

type FetchReportsDomainDataEpicObservable = Observable<UpdateReportsDomain | ShowCommonDataFetchError>;
type AddReportDomainEpicObservable = Observable<FetchReportsDomain | StopReportsDomainLoading>;

const fetchReportsDomainDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_REPORTS_DOMAIN),
        mergeMap(
            (): FetchReportsDomainDataEpicObservable => {
                return from(deps.reportsDomainDataProvider.fetchReportsDomainData()).pipe(
                    mergeMap(data => {
                        const reports = mapReportsDomainApiDataToClientData(data.offlineReportList.list);

                        return of(updateReportsDomain(reports));
                    }),
                    catchError((err: ApiMethodExecutingError) => {
                        return of(showCommonDataFetchError(err));
                    })
                );
            }
        )
    );
};

const addReportDomainEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ADD_REPORT_DOMAIN),
        mergeMap(
            (): AddReportDomainEpicObservable => {
                const {
                    reportDomain: { periodValue }
                } = store$.value;

                const dates = mapReportsDomainDatesToApiFormat(periodValue);

                const request: GdOfflineReportResultRequestInput = {
                    monthTo: dates.monthTo,
                    monthFrom: dates.monthFrom,
                    reportType: OfflineReportTypeInput.DOMAINS
                };

                return from(deps.reportsDomainDataProvider.addReportsDomain(request)).pipe(
                    mergeMap(response => {
                        if (response.errors && response.errors.length === 0) {
                            showNoticeMessage(NoticeType.error, i18n('request-error'));
                        } else {
                            showNoticeMessage(NoticeType.success, getReportsSuccessTexts('addReport'));
                        }

                        return of(fetchReportsDomain());
                    }),
                    catchError((error: ApiMethodExecutingError) => {
                        const errorMessage = error.getMessage();
                        const errorText = typeof error === 'string' ? errorMessage : i18n('request-error');
                        showNoticeMessage(NoticeType.error, errorText);
                        return of(stopReportsDomainLoading);
                    })
                );
            }
        )
    );
};

export { fetchReportsDomainDataEpic, addReportDomainEpic };
